import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormControlName,
  FormGroupDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
} from '@angular/forms';
import { DatePipe } from '@angular/common';
import { NgbDate, NgbDatepicker, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { UiComponentsModule } from '@mwe/ui/components';
import {
  DATE_FORMAT_GERMAN,
  DATE_FORMAT_HEADER,
  DATE_SEPARATOR,
  DOT_REGEX,
  EMPTY_DATE_TITLE,
  EMPTY_STRING,
  GERMAN_LOCALE,
} from '../../app.constants';
import { getDayJsFromNgbDateStruct } from '@mwe/utils';
import { resolveTranslationKey } from '../../utils/signal-forms.util';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '@mwe/constants';
import { DateInputMaskDirective } from '../../directives/date-input-mask.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { CdkTrapFocus } from '@angular/cdk/a11y';

/**
 * The datepicker can be used as a standalone component or as a part of a form.
 * If used as a standalone component, one should provide [ngModel] and (ngModelChange).
 * For the form part, the [formControlName] is enough.
 *
 * @example
 * <lib-ikp-datepicker
 *     id="dateInput" // required
 *     label="label" // optional
 *     placeholder="placeholder" // optional
 *     [maximumDate]="maxDate" // optional
 *     [minimumDate]="minDate" // optional
 *     [ngModel]="model()"
 *     (ngModelChange)="datepickerChange($event)"/>
 *
 *  <form [formGroup]="form">
 *    <lib-ikp-datepicker
 *      id="dateInput" // required
 *      label="label" // optional
 *      placeholder="placeholder" // optional
 *      [maximumDate]="maxDate" // optional
 *      [minimumDate]="minDate" // optional
 *      formControlName="dateInput" // required
 *    />
 *  </form>
 */
@Component({
  selector: 'lib-ikp-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss',
  standalone: true,
  providers: [
    {
      provide: DatePipe,
      useFactory: () => new DatePipe(GERMAN_LOCALE),
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DatepickerComponent,
      multi: true,
    },
  ],
  imports: [CdkTrapFocus, TranslatePipe, NgbDatepicker, FormsModule, UiComponentsModule, DateInputMaskDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerComponent implements ControlValueAccessor, OnInit {
  public id = input.required<string>();
  public label = input<string>('global.input-labels.datepicker');
  public placeholder = input<string>('');
  public maximumDate = input<NgbDateStruct>({ day: 0, month: 0, year: 0 });
  public minimumDate = input<NgbDateStruct>({ day: 0, month: 0, year: 0 });
  public dataTest = input<string>();

  // Initilized as empty then assigned by the CVA callback
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: (value: NgbDateStruct) => void = () => {};

  // Initilized as empty then assigned by the CVA callback
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  protected control!: FormControl;
  protected value: NgbDateStruct;
  protected isCalendarOpen = signal<boolean>(false);
  protected rawInput: string | null = null;

  private datePipe = inject(DatePipe);
  private injector = inject(Injector);
  private inputField = viewChild.required<ElementRef<HTMLInputElement>>('inputElement');

  @HostListener('keydown', ['$event'])
  handleEnterPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.openCalendar();
    }
  }

  public ngOnInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    const formGroupDirective = this.injector.get(FormGroupDirective, null);

    if (ngControl instanceof FormControlName && formGroupDirective) {
      this.control = formGroupDirective.getControl(ngControl);
    } else if (ngControl?.control) {
      this.control = ngControl.control as FormControl;
    }
  }

  writeValue(obj: NgbDateStruct): void {
    if (!this.isSameDate(obj, this.value)) {
      this.rawInput = null;
    }
    this.value = obj;
  }

  registerOnChange(fn: (value: NgbDateStruct) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  protected toggleCalendar(): void {
    if (this.isCalendarOpen()) {
      this.closeCalendar();
    } else {
      this.openCalendar();
    }
  }

  protected openCalendar(): void {
    this.isCalendarOpen.set(true);
  }

  protected closeCalendar(): void {
    this.isCalendarOpen.set(false);
    this.onTouched();
  }

  protected handleBlur(): void {
    this.rawInput = null;
    this.onTouched();
  }

  protected handleInputEvent(event: Event): void {
    const input = (event.target as HTMLInputElement).value;
    const parsed = this.parseInputString(input);

    const isEmpty = input === EMPTY_STRING && Object.values(parsed).every(v => v === null);
    const valueToSet = isEmpty ? null : parsed || null;

    this.rawInput = this.isCompleteInput(input) ? null : input;
    this.value = valueToSet;
    this.onChange(valueToSet);
  }

  protected handleDateSelect(event: NgbDate): void {
    this.rawInput = null;
    this.value = event;
    this.onChange(event);
  }

  protected get dateHeader(): string {
    if (!this.value) return EMPTY_DATE_TITLE;
    const currentDate = getDayJsFromNgbDateStruct(this.value);
    if (currentDate.isValid()) {
      const pipedDate = this.datePipe.transform(currentDate.format(DATE_FORMAT), DATE_FORMAT_HEADER, undefined, GERMAN_LOCALE);
      return this.cleanDate(pipedDate);
    }
    return EMPTY_DATE_TITLE;
  }

  protected get yearHeader(): number {
    return this.value?.year || dayjs().year();
  }

  protected get valueFormatted(): string {
    if (this.rawInput !== null) {
      return this.rawInput;
    }

    if (!(this.value?.day && this.value?.month && this.value?.year)) {
      return this.inputField().nativeElement.value;
    }

    const formattedDate = getDayJsFromNgbDateStruct(this.value);
    return formattedDate ? formattedDate.format(DATE_FORMAT_GERMAN) : EMPTY_STRING;
  }

  protected get isDateInvalid(): boolean {
    return this.control && this.control.invalid && this.control.touched;
  }

  protected get errorMessage(): string {
    return resolveTranslationKey(this.control?.errors);
  }

  /**
   * Checks if the input is an 8-digit number (DDMMYYYY); if so,
   * adds '.' separators to format it as DD.MM.YYYY, then parses and validates it as an NgbDateStruct.
   */
  private parseInputString(input: string): NgbDateStruct {
    const NUMBER_ONLY = /^\d{8}$/;
    const DOT_REPLACE = /(\d{2})(\d{2})(\d{4})/;
    const DATE_FORMAT = '$1.$2.$3';

    const formattedInput = NUMBER_ONLY.test(input) ? input.replace(DOT_REPLACE, DATE_FORMAT) : input;
    return this.parseStringToNGBDateStruct(formattedInput);
  }

  private isSameDate(a: NgbDateStruct | null, b: NgbDateStruct | null): boolean {
    if (!a || !b) { return a === b; }
    return a.day === b.day && a.month === b.month && a.year === b.year;
  }

  private isCompleteInput(input: string): boolean {
    return /^\d{8}$/.test(input) || /^\d{2}\.\d{2}\.\d{4}$/.test(input);
  }

  private cleanDate(date: string | null): string {
    return date ? date.replace(DOT_REGEX, EMPTY_STRING) : EMPTY_STRING;
  }

  private parseStringToNGBDateStruct(value: string): NgbDateStruct {
    const [dayStr, monthStr, yearStr] = value.split(DATE_SEPARATOR);

    const day = parseInt(dayStr || '', 10);
    const month = parseInt(monthStr || '', 10);
    const year = parseInt(yearStr || '', 10);

    return {
      day: day > 0 && day <= 31 ? day : null,
      month: month > 0 && month <= 12 ? month : null,
      year: year > 999 ? year : null,
    };
  }
}
